//@ts-nocheck
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { ArrowLeft, MagnifyingGlass, X } from '@phosphor-icons/react';
import { XGModal } from 'xg-cl';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { searchStockPhotos } from '../../../services/photo/photo';
import StockPhotoGrid, { PhotoCredit } from './StockPhotoGrid';
import StockPhotoImage from './StockPhotoImage';
import { STOCK_PHOTO_MAX_IMAGE_SIZE } from '../../../constants/files';
import {
  SUPPORTED_IMAGE_EXTENSIONS,
  getSupportedFormatsString,
  isSupportedImageFile
} from '../../../utils/supportedImages';
import './PhotoPickerModal.css';
import '../../GlobalSettings/PassengerFieldsPanel/PassengerFieldsPanel/PassengerFieldsPanel.css';

const PAGE_SIZE = 18;
const SEARCH_DELAY_MS = 350;

const PhotoPickerModal = ({
  open,
  onClose,
  onConfirm,
  onUploadConfirm,
  destinationTerm = '',
  tripTitle = '',
  orientation = 'wide',
  multiple = false,
  remainingSlots = 1
}) => {
  const { t } = useTranslation('common');
  const { enableStockPhotoPicker = false } = useFlags();
  const [source, setSource] = useState('upload');
  const [stockOpened, setStockOpened] = useState(false);
  const [queryInput, setQueryInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchRevision, setSearchRevision] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [emptySeed, setEmptySeed] = useState('');
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);
  const requestId = useRef(0);
  const resolvedQuery = useRef('');
  const loadMoreController = useRef(null);
  const loadMoreInFlight = useRef(false);
  const browseRef = useRef(null);
  const scrollSentinelRef = useRef(null);
  const seedTerm = (destinationTerm || tripTitle || '').trim();
  const activePhoto = selected[selected.length - 1];
  const providerPhoto = photos.find(
    (photo) => photo.provider && photo.photoPageUrl
  );
  let providerSiteUrl = '';
  if (providerPhoto) {
    try {
      const url = new URL(providerPhoto.photoPageUrl);
      if (url.protocol === 'https:') providerSiteUrl = url.origin;
    } catch {
      // Search results without a valid provider URL still show their photo credits.
    }
  }
  const providerName = providerPhoto?.provider
    ? providerPhoto.provider.charAt(0).toUpperCase() +
      providerPhoto.provider.slice(1)
    : '';
  const sources = [
    { key: 'upload', label: t('photoPicker.upload') },
    { key: 'stock', label: t('photoPicker.stockPhotos') }
  ];

  useEffect(() => {
    if (!open) return;
    setSource('upload');
    setStockOpened(false);
    setQueryInput('');
    setSearchQuery('');
    setSearchRevision(0);
    setPhotos([]);
    setSelected([]);
    setUploadFiles([]);
    setUploadError('');
    setDetailOpen(false);
    setSearchError(false);
    setLoadMoreError(false);
    setSubmitError(false);
    setEmptySeed('');
    setPage(1);
    setHasNextPage(false);
    return () => {
      requestId.current += 1;
      loadMoreController.current?.abort();
    };
  }, [open]);

  useEffect(() => {
    if (!open || source !== 'stock' || !stockOpened) return;
    const timer = window.setTimeout(() => {
      setSearchQuery(queryInput.trim());
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [open, source, stockOpened, queryInput]);

  useEffect(() => {
    if (!open || source !== 'stock' || !stockOpened) return;
    const controller = new AbortController();
    const currentRequest = ++requestId.current;
    loadMoreController.current?.abort();
    loadMoreController.current = null;
    loadMoreInFlight.current = false;
    if (browseRef.current) browseRef.current.scrollTop = 0;
    setLoading(true);
    setLoadingMore(false);
    setSearchError(false);
    setLoadMoreError(false);
    setEmptySeed('');
    setPhotos([]);
    setHasNextPage(false);

    const runSearch = async () => {
      try {
        let query = searchQuery;
        let result = await searchStockPhotos({
          query,
          orientation,
          page: 1,
          perPage: PAGE_SIZE,
          signal: controller.signal
        });
        if (result.photos.length === 0 && query && query === seedTerm) {
          query = '';
          result = await searchStockPhotos({
            query,
            orientation,
            page: 1,
            perPage: PAGE_SIZE,
            signal: controller.signal
          });
          if (currentRequest === requestId.current) setEmptySeed(searchQuery);
        }
        if (currentRequest !== requestId.current) return;
        resolvedQuery.current = query;
        setPhotos(result.photos);
        setHasNextPage(result.hasNextPage);
      } catch (error) {
        if (currentRequest !== requestId.current || controller.signal.aborted)
          return;
        setSearchError(true);
      } finally {
        if (currentRequest === requestId.current) setLoading(false);
      }
    };
    runSearch();
    return () => {
      controller.abort();
      requestId.current += 1;
    };
  }, [
    open,
    source,
    stockOpened,
    searchQuery,
    searchRevision,
    seedTerm,
    orientation
  ]);

  const changeSource = (nextSource) => {
    setSource(nextSource);
    setDetailOpen(false);
    setSubmitError(false);
    if (nextSource === 'stock' && !stockOpened) {
      setStockOpened(true);
      setQueryInput(seedTerm);
      setSearchQuery(seedTerm);
    }
  };

  const searchNow = () => {
    setSearchQuery(queryInput.trim());
    setSearchRevision((revision) => revision + 1);
    setPage(1);
  };

  const selectPhoto = (photo) => {
    const key = `${photo.provider}:${photo.providerPhotoId}`;
    const existingIndex = selected.findIndex(
      (item) => `${item.provider}:${item.providerPhotoId}` === key
    );
    if (existingIndex !== -1) {
      setSelected(selected.filter((_, index) => index !== existingIndex));
      setDetailOpen(false);
    } else if (!multiple && remainingSlots > 0) {
      setSelected([photo]);
      setDetailOpen(true);
    } else if (selected.length < remainingSlots) {
      setSelected([...selected, photo]);
      setDetailOpen(true);
    }
  };

  const loadMore = useCallback(
    async (retry = false) => {
      if (
        loading ||
        loadingMore ||
        loadMoreInFlight.current ||
        !hasNextPage ||
        searchError ||
        (loadMoreError && !retry) ||
        queryInput.trim() !== searchQuery
      )
        return;
      const controller = new AbortController();
      loadMoreController.current = controller;
      loadMoreInFlight.current = true;
      const currentRequest = requestId.current;
      const nextPage = page + 1;
      setLoadingMore(true);
      setLoadMoreError(false);
      try {
        const result = await searchStockPhotos({
          query: resolvedQuery.current,
          orientation,
          page: nextPage,
          perPage: PAGE_SIZE,
          signal: controller.signal
        });
        if (currentRequest !== requestId.current || controller.signal.aborted)
          return;
        setPhotos((items) => [...items, ...result.photos]);
        setHasNextPage(result.hasNextPage);
        setPage(nextPage);
      } catch (error) {
        if (!controller.signal.aborted && currentRequest === requestId.current)
          setLoadMoreError(true);
      } finally {
        if (currentRequest === requestId.current) setLoadingMore(false);
        if (loadMoreController.current === controller) {
          loadMoreController.current = null;
          loadMoreInFlight.current = false;
        }
      }
    },
    [
      hasNextPage,
      loadMoreError,
      loading,
      loadingMore,
      orientation,
      page,
      queryInput,
      searchError,
      searchQuery
    ]
  );

  useEffect(() => {
    if (
      !open ||
      source !== 'stock' ||
      !stockOpened ||
      loading ||
      loadingMore ||
      searchError ||
      loadMoreError ||
      !hasNextPage ||
      !browseRef.current ||
      !scrollSentinelRef.current ||
      typeof IntersectionObserver === 'undefined'
    )
      return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { root: browseRef.current, rootMargin: '0px 0px 160px 0px' }
    );
    observer.observe(scrollSentinelRef.current);
    return () => observer.disconnect();
  }, [
    hasNextPage,
    loadMore,
    loadMoreError,
    loading,
    loadingMore,
    open,
    searchError,
    source,
    stockOpened
  ]);

  const confirm = async () => {
    const files = source === 'upload' ? uploadFiles : selected;
    if (!files.length || submitting) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      const result =
        source === 'upload'
          ? await onUploadConfirm(files)
          : await onConfirm(selected);
      if (result === false) setSubmitError(true);
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const stageFiles = (nextFiles) => {
    const files = Array.from(nextFiles || []);
    if (!files.length) return;
    if (files.length + uploadFiles.length > remainingSlots) {
      setUploadError(
        t('photoPicker.tooManyPhotos', {
          count: remainingSlots,
          defaultValue_one: 'Only 1 photo can be added.',
          defaultValue_other: 'Only {{count}} photos can be added.'
        })
      );
      return;
    }
    const invalid = files.find((file) => !isSupportedImageFile(file));
    if (invalid) {
      setUploadError(
        t('imageFormatNotSupported', { formats: getSupportedFormatsString() })
      );
      return;
    }
    if (files.some((file) => file.size > STOCK_PHOTO_MAX_IMAGE_SIZE)) {
      setUploadError(t('imageFileTooBig', { maxImageSize: 20 }));
      return;
    }
    setUploadError('');
    setUploadFiles((current) => [...current, ...files]);
  };

  const selectedCount =
    source === 'upload' ? uploadFiles.length : selected.length;

  if (!enableStockPhotoPicker) return null;

  return (
    <XGModal
      id='photo-picker-modal'
      className='photo-picker'
      labelledby='photo-picker-title'
      title={
        <div className='photo-picker__heading'>
          <strong>
            {t('photoPicker.addImages', { defaultValue: 'Add images' })}
          </strong>
        </div>
      }
      open={open}
      onClose={onClose}
      allowClosing={!submitting}
      dismissable={false}
      actions={
        <>
          <Button
            className='photo-picker__cancel'
            variant='outlined'
            onClick={onClose}
            disabled={submitting}
          >
            {t('cancel')}
          </Button>

          <div style={{ position: 'relative' }}>
            {submitting && (
              <CircularProgress
                id='photo-picker__saving-progress-indicator'
                size={24}
                className='spinner spinner__primary'
              />
            )}

            <Button
              className='photo-picker__confirm'
              variant='contained'
              onClick={confirm}
              disabled={!selectedCount || submitting}
              aria-busy={submitting}
            >
              <span className='photo-picker__confirm-content'>
                <span className='photo-picker__confirm-label'>
                  {t('photoPicker.addPhoto', {
                    count: selectedCount || 1,
                    defaultValue_one: 'Add photo',
                    defaultValue_other: 'Add {{count}} photos'
                  })}
                </span>
              </span>
            </Button>
          </div>
        </>
      }
    >
      <div
        className='photo-picker__tabs passenger-fields-panel__flag-tabs-nav'
        role='tablist'
        aria-label={t('photoPicker.sources')}
      >
        {sources.map(({ key, label }) => (
          <button
            key={key}
            type='button'
            role='tab'
            aria-selected={source === key}
            className={`passenger-fields-panel__flag-tab${source === key ? ' passenger-fields-panel__flag-tab--active' : ''}`}
            onClick={() => changeSource(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {submitError && (
        <p className='photo-picker__submit-error' role='alert'>
          {t('photoPicker.selectionFailed')}
        </p>
      )}
      {source === 'upload' ? (
        <div className='photo-picker__upload'>
          <div
            className='photo-picker__dropzone'
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              stageFiles(event.dataTransfer?.files);
            }}
          >
            <span>
              {t('photoPicker.dragAndDrop', {
                defaultValue: 'Drag and drop or'
              })}
            </span>
            <Button component='label' variant='contained'>
              {t('photoPicker.uploadImage', {
                defaultValue: 'Upload Image'
              })}
              <input
                type='file'
                hidden
                multiple={multiple}
                accept={SUPPORTED_IMAGE_EXTENSIONS}
                onChange={(event) => {
                  stageFiles(event.target.files);
                  event.target.value = '';
                }}
              />
            </Button>
            <small>
              {t('photoPicker.maxFileSize', {
                defaultValue:
                  'Max file size for photos is 20MB. Please resize if needed.'
              })}
            </small>
          </div>
          {uploadFiles.length > 0 && (
            <div className='photo-picker__staged-files'>
              {uploadFiles.map((file, index) => (
                <div key={`${file.name}-${index}`}>
                  <span>{file.name}</span>
                  <Tooltip
                    title={t('photoPicker.removePhoto', {
                      defaultValue: 'Remove photo'
                    })}
                  >
                    <IconButton
                      aria-label={t('photoPicker.removePhoto', {
                        defaultValue: 'Remove photo'
                      })}
                      onClick={() =>
                        setUploadFiles((current) =>
                          current.filter((_, fileIndex) => fileIndex !== index)
                        )
                      }
                    >
                      <X size={16} />
                    </IconButton>
                  </Tooltip>
                </div>
              ))}
            </div>
          )}
          {uploadError && (
            <p className='photo-picker__upload-error' role='alert'>
              {uploadError}
            </p>
          )}
        </div>
      ) : (
        <div
          className={`photo-picker__stock${detailOpen ? ' photo-picker__stock--detail-open' : ''}`}
        >
          <div className='photo-picker__browse' ref={browseRef}>
            <div className='photo-picker__search-row'>
              <div className='photo-picker__search-field'>
                <Tooltip title={t('photoPicker.search')}>
                  <IconButton
                    aria-label={t('photoPicker.search')}
                    onClick={searchNow}
                  >
                    <MagnifyingGlass size={17} />
                  </IconButton>
                </Tooltip>
                <input
                  type='search'
                  id='photo-picker-search'
                  name='photoPickerSearch'
                  aria-label={t('photoPicker.search')}
                  value={queryInput}
                  placeholder={t('photoPicker.searchPlaceholder')}
                  onChange={(event) => setQueryInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') searchNow();
                  }}
                />
                {queryInput && (
                  <Tooltip title={t('photoPicker.clearSearch')}>
                    <IconButton
                      aria-label={t('photoPicker.clearSearch')}
                      onClick={() => {
                        setQueryInput('');
                        setSearchQuery('');
                        setSearchRevision((revision) => revision + 1);
                        setPage(1);
                      }}
                    >
                      <X size={16} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              {!loading && !searchError && (
                <span className='photo-picker__result-count'>
                  {t('photoPicker.photoCount', {
                    count: photos.length,
                    defaultValue_one: '1 photo',
                    defaultValue_other: '{{count}} photos'
                  })}
                </span>
              )}
              {providerSiteUrl && (
                <a
                  className='photo-picker__provider-link'
                  href={providerSiteUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {providerName}
                </a>
              )}
            </div>
            {emptySeed && (
              <p className='photo-picker__notice'>
                {t('photoPicker.showingPopular', { term: emptySeed })}
              </p>
            )}
            {searchError ? (
              <p className='photo-picker__notice' role='alert'>
                {t('photoPicker.searchUnavailable')}
              </p>
            ) : photos.length === 0 && !loading ? (
              <p className='photo-picker__notice'>
                {t('photoPicker.noResults')}
              </p>
            ) : null}
            <StockPhotoGrid
              photos={photos}
              selected={selected}
              remainingSlots={remainingSlots}
              multiple={multiple}
              onSelect={selectPhoto}
              loading={loading}
              loadingMore={loadingMore}
            />
            {loadMoreError && (
              <div className='photo-picker__page-error' role='alert'>
                <span>
                  {t('photoPicker.moreUnavailable', {
                    defaultValue: 'Could not load more photos.'
                  })}
                </span>
                <Button onClick={() => loadMore(true)}>
                  {t('photoPicker.retry', { defaultValue: 'Retry' })}
                </Button>
              </div>
            )}
            {hasNextPage && !loading && !searchError && !loadMoreError && (
              <div
                ref={scrollSentinelRef}
                className='photo-picker__scroll-sentinel'
                aria-hidden='true'
              />
            )}
          </div>
          <aside className='photo-picker__detail'>
            <Tooltip title={t('photoPicker.backToResults')}>
              <IconButton
                className='photo-picker__back'
                aria-label={t('photoPicker.backToResults')}
                onClick={() => setDetailOpen(false)}
              >
                <ArrowLeft size={20} />
              </IconButton>
            </Tooltip>
            <span className='photo-picker__preview-label'>
              {t('photoPicker.preview', { defaultValue: 'Preview' })}
            </span>
            <div
              className='photo-picker__detail-image'
              role={activePhoto ? 'img' : undefined}
              aria-label={activePhoto?.altText || t('photoPicker.photo')}
            >
              {activePhoto && (
                <StockPhotoImage
                  key={`${activePhoto.provider}:${activePhoto.providerPhotoId}:${activePhoto.previewUrl}:${activePhoto.thumbnailUrl}`}
                  sources={[activePhoto.previewUrl, activePhoto.thumbnailUrl]}
                />
              )}
            </div>
            {activePhoto && (
              <div className='photo-picker__detail-meta'>
                <PhotoCredit photo={activePhoto} />
                <span>
                  {activePhoto.width.toLocaleString()} ×{' '}
                  {activePhoto.height.toLocaleString()}
                </span>
              </div>
            )}
          </aside>
        </div>
      )}
    </XGModal>
  );
};

PhotoPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onUploadConfirm: PropTypes.func.isRequired,
  destinationTerm: PropTypes.string,
  tripTitle: PropTypes.string,
  orientation: PropTypes.oneOf(['wide', 'tall', 'any']),
  multiple: PropTypes.bool,
  remainingSlots: PropTypes.number
};

export default PhotoPickerModal;
